import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  horizontalListSortingStrategy,
  SortableContext
} from '@dnd-kit/sortable';
import type { ColumnsType } from 'antd/es/table';
import type { ComponentType, ReactElement, ReactNode } from 'react';

import { DRAG_ACTIVATION_DISTANCE } from '../../constants';

import { SortableHeaderCell } from './components';
import { useReorderableColumns } from './hooks';
import type { ColumnReorderRenderProps } from './types';

export type Props<T> = {
  enabled: boolean;
  columns?: ColumnsType<T>;
  columnOrder: string[] | undefined;
  onColumnOrderChange: (order: string[] | undefined) => void;
  children: (renderProps: ColumnReorderRenderProps<T>) => ReactElement;
};

const ColumnReorderProvider = <T,>({
  enabled,
  columns,
  columnOrder,
  onColumnOrderChange,
  children
}: Props<T>) => {
  const { orderedColumns, reorderableKeys, handleDragEnd } =
    useReorderableColumns<T>({
      columns,
      columnOrder,
      enabled,
      setColumnOrder: onColumnOrderChange
    });

  const dndSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE }
    })
  );

  const headerComponents = enabled
    ? { header: { cell: SortableHeaderCell as ComponentType } }
    : undefined;

  const wrap = (node: ReactNode): ReactNode => {
    if (!enabled) return node;

    return (
      <DndContext sensors={dndSensors} onDragEnd={handleDragEnd}>
        <SortableContext
          items={reorderableKeys}
          strategy={horizontalListSortingStrategy}>
          {node}
        </SortableContext>
      </DndContext>
    );
  };

  return children({ orderedColumns, headerComponents, wrap });
};

export default ColumnReorderProvider;
